import { v4 as uuidv4 } from 'uuid';
import { supabase, getCurrentUser } from '../lib/supabase';
import { getLogger } from './logger';

// Create a namespaced logger
const logger = getLogger('Analytics');

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export type AnalyticsEvent = {
  type: 'page_view' | 'click' | 'form_submit' | 'assessment' | 'error' | 'custom';
  name: string;
  data?: Record<string, any>;
  timestamp?: string;
};

export type AnalyticsSession = {
  id: string;
  startTime: string;
  endTime?: string;
  events: AnalyticsEvent[];
};

class Analytics {
  private sessionId: string | null = null;
  private sessionStartTime: string | null = null;
  private events: AnalyticsEvent[] = [];
  private initialized = false;
  private readonly MAX_RETRIES = 3;
  private retryDelay = 100; // Start with 100ms delay
  private initPromise: Promise<void> | null = null;

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async createSession(retryCount = 0): Promise<boolean> {
    try {
      const user = await getCurrentUser();
      
      if (!user) {
        logger.log('No authenticated user found, skipping analytics session creation');
        return false;
      }

      // Wait for a short delay to ensure auth is fully initialized
      await this.delay(100);

      const newSessionId = uuidv4();
      const newStartTime = new Date().toISOString();

      logger.log('Creating analytics session:', newSessionId);
      const { error } = await supabase.from('analytics_sessions').insert({
        id: newSessionId,
        user_id: user.id,
        start_time: newStartTime,
        user_agent: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        platform: navigator.platform
      });

      if (error) {
        if (error.code === '23505' && retryCount < this.MAX_RETRIES) {
          logger.warn('Session ID conflict, retrying with new ID...');
          await this.delay(this.retryDelay * Math.pow(2, retryCount));
          return await this.createSession(retryCount + 1);
        }
        logger.error('Failed to create analytics session:', error);
        return false;
      }

      this.sessionId = newSessionId;
      this.sessionStartTime = newStartTime;
      logger.log('Analytics session created successfully');
      return true;
    } catch (error) {
      logger.error('Failed to create analytics session:', error);
      return false;
    }
  }

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    if (this.initialized) return Promise.resolve();

    this.initPromise = new Promise<void>(async (resolve) => {
      try {
        logger.log('Initializing analytics...');
        const user = await getCurrentUser();
        
        if (user) {
          const success = await this.createSession();
          if (success) {
            this.initialized = true;
            logger.log('Analytics initialized successfully');
            window.addEventListener('beforeunload', () => {
              this.endSession().catch(err => logger.error('Error ending session:', err));
            });
          } else {
            logger.warn('Failed to initialize analytics');
          }
        } else {
          logger.log('No authenticated user, skipping analytics initialization');
        }
      } catch (error) {
        logger.error('Failed to initialize analytics:', error);
      } finally {
        this.initPromise = null;
        resolve();
      }
    });

    return this.initPromise;
  }

  async trackEvent(event: Omit<AnalyticsEvent, 'timestamp'>) {
    await this.init();
    if (!this.sessionId) {
      logger.log('No active session, skipping event tracking');
      return;
    }

    const timestamp = new Date().toISOString();
    const fullEvent = { ...event, timestamp };
    this.events.push(fullEvent);

    try {
      const user = await getCurrentUser();
      if (user) {
        // Track in Supabase
        logger.log('Tracking event:', event.name);
        await supabase.from('analytics_events').insert({
          session_id: this.sessionId,
          user_id: user.id,
          event_type: event.type,
          event_name: event.name,
          event_data: event.data || {},
          timestamp
        });

        // Track in Google Analytics
        if (window.gtag) {
          logger.log('Sending event to Google Analytics:', event.name);
          window.gtag('event', event.name, {
            event_category: event.type,
            ...event.data
          });
        }
      }
    } catch (error) {
      logger.error('Failed to track event:', error);
    }
  }

  async trackPageView(path: string) {
    // Track in Google Analytics
    if (window.gtag) {
      logger.log('Sending page view to Google Analytics:', path);
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href
      });
    }

    // Track in our analytics system
    await this.trackEvent({
      type: 'page_view',
      name: path,
      data: {
        referrer: document.referrer,
        title: document.title,
        url: window.location.href
      }
    });
  }

  async trackClick(elementId: string, elementType: string, data?: Record<string, any>) {
    logger.log('Tracking click:', elementId);
    await this.trackEvent({
      type: 'click',
      name: `${elementType}_click`,
      data: {
        elementId,
        ...data
      }
    });
  }

  async trackFormSubmit(formId: string, data?: Record<string, any>) {
    logger.log('Tracking form submit:', formId);
    await this.trackEvent({
      type: 'form_submit',
      name: formId,
      data
    });
  }

  async trackError(error: Error, context?: Record<string, any>) {
    logger.error('Tracking error:', error.message);
    await this.trackEvent({
      type: 'error',
      name: error.name,
      data: {
        message: error.message,
        stack: error.stack,
        ...context
      }
    });
  }

  async endSession() {
    if (!this.initialized || !this.sessionId) return;

    try {
      logger.log('Ending analytics session:', this.sessionId);
      const user = await getCurrentUser();
      if (user) {
        await supabase.from('analytics_sessions').update({
          end_time: new Date().toISOString(),
          events_count: this.events.length
        }).eq('id', this.sessionId);
        logger.log('Session ended successfully');
      }
    } catch (error) {
      logger.error('Failed to end analytics session:', error);
    }
  }
}

export const analytics = new Analytics();

// Initialize analytics when the module is imported
analytics.init().catch(err => logger.error('Error initializing analytics:', err));